import {
  createTheme,
  ThemeProvider as MuiThemeProvider,
  responsiveFontSizes,
  SimplePaletteColorOptions,
} from '@mui/material/styles';
import { ReactElement } from 'react';

declare module '@mui/material/styles' {
  interface Palette {
    cdmBrand: Palette['primary'];
    cdmBrandSecondary: Palette['primary'];
    cdmBrandThird: Palette['primary'];
    cdmBrandYellow: Palette['primary'];
    cdmBrandOrange: Palette['primary'];
  }
  interface PaletteOptions {
    cdmBrand: PaletteOptions['primary'];
    cdmBrandSecondary: PaletteOptions['primary'];
    cdmBrandThird: PaletteOptions['primary'];
    cdmBrandYellow: PaletteOptions['primary'];
    cdmBrandOrange: PaletteOptions['primary'];
  }
}

declare module '@mui/material' {
  interface ButtonPropsColorOverrides {
    cdmBrand: true;
    cdmBrandSecondary: true;
    cdmBrandThird: true;
    cdmBrandYellow: true;
    cdmBrandOrange: true;
  }

  interface IconButtonPropsColorOverrides {
    cdmBrand: true;
    cdmBrandSecondary: true;
    cdmBrandThird: true;
    cdmBrandYellow: true;
    cdmBrandOrange: true;
  }

  interface ChipPropsColorOverrides {
    cdmBrand: true;
    cdmBrandSecondary: true;
    cdmBrandThird: true;
    cdmBrandYellow: true;
    cdmBrandOrange: true;
  }
}

let theme = createTheme({
  typography: {
    fontFamily: `"Titillium Web", "Helvetica", "Arial", sans-serif`,
    fontWeightRegular: 400,
  },
  palette: {
    primary: {
      main: '#475156',
    },
    secondary: {
      main: '#707070',
    },
    cdmBrandYellow: {
      main: '#ffd000',
      contrastText: '#ffffff',
    },
    cdmBrandOrange: {
      main: '#ED7D31',
      contrastText: '#ffffff',
    },
    cdmBrand: {
      main: '#d6012e',
      contrastText: '#ffffff',
    },
    cdmBrandSecondary: {
      main: '#23856c',
      contrastText: '#ffffff',
    },
    cdmBrandThird: {
      main: '#127db5',
      contrastText: '#ffffff',
    },
  },
  components: {
    MuiGrid: {
      styleOverrides: {
        root: {
          marginTop: '0',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderWidth: '2px !important',
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        root: {
          '.MuiOutlinedInput-root': {
            paddingBottom: '7.5px !important',
            paddingTop: '7.5px !important',
          },
        },
      },
    },
    MuiFormControl: {
      styleOverrides: {
        root: {
          '.MuiInputLabel-outlined': {
            fontSize: '1.15rem',
          },
          '.MuiInputLabel-outlined.Mui-disabled': {
            fontSize: '1.15rem',
            color: '#000',
          },
          '.MuiOutlinedInput-notchedOutline': {
            fontSize: '1.15em !important',
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          input: {
            padding: '10px 14px',
          },
          '.MuiSelect-select': {
            backgroundColor: 'white',
            padding: '10px 14px',
          },
          '.Mui-disabled ~ fieldset': {
            borderWidth: '2px !important',
          },
          '.MuiSelect-select.Mui-disabled': {
            color: '#000',
            WebkitTextFillColor: 'unset',
            backgroundColor: '#fafafa !important',
          },
          '.MuiSelect-select.MuiInputLabel-outlined': {
            fontSize: '1.15rem',
          },
          '.Mui-focused': {
            fontSize: '1.15rem !important',
          },
        },
      },
    },
    MuiFormControlLabel: {
      styleOverrides: {
        root: {
          '.MuiFormControlLabel-label.Mui-disabled': {
            color: '#000',
            WebkitTextFillColor: 'unset',
          },
          '.MuiFormLabel-filled': {
            fontSize: '1.15rem',
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '.MuiInputLabel-outlined.Mui-focused': {
            fontSize: '1.15rem !important',
          },
          '.MuiFormLabel-filled ': {
            fontSize: '1.15rem !important',
          },
          '.MuiOutlinedInput-notchedOutline': {
            fontSize: '1.15em !important',
          },
          '.MuiOutlinedInput-notchedOutline.Mui-focused': {
            fontSize: '1.15em !important',
          },
          '.Mui-disabled.MuiInput-input': {
            color: '#000',
            WebkitTextFillColor: 'unset',
            backgroundColor: '#fafafa !important',
          },
          '.MuiOutlinedInput-input.Mui-disabled': {
            color: '#000',
            WebkitTextFillColor: 'unset',
          },
          '.MuiOutlinedInput-input.Mui-disabled::placeholder': {
            color: '#000',
            WebkitTextFillColor: 'unset',
          },
          '.MuiOutlinedInput-root': {
            backgroundColor: 'white',
          },
          '.MuiOutlinedInput-root.Mui-disabled': {
            backgroundColor: '#fafafa !important',
          },
          '.MuiFormHelperText-filled': {
            fontSize: '0.85rem',
          },
        },
      },
    },
  },
});

theme = responsiveFontSizes(theme);

interface ThemeProviderProps {
  children: ReactElement;
}

export const ThemeProvider = ({ children }: ThemeProviderProps): ReactElement => (
  <MuiThemeProvider theme={theme}>{children}</MuiThemeProvider>
);

ThemeProvider.displayName = 'ThemeProvider';

type FilteredKeys<T, U> = { [P in keyof T]: T[P] extends U ? P : never }[keyof T];

type FilteredProperties<T, U> = { [K in FilteredKeys<T, U>]: T[K] };

type PullOutPaletteColorOptions<T> = FilteredProperties<T, SimplePaletteColorOptions>;

export type AnprThemeColor = keyof PullOutPaletteColorOptions<typeof theme.palette>;
